import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllAccounts } from "../expense/accounts/AccountService";
import { getAllItems, updateItem, updateItemDate, updateItemStatus } from "./PlannerService";
import "./PaymentsDashboard.css";

function parseDate(d) {
  if (!d) return null;
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function daysBetween(today, date) {
  if (!date) return Number.MAX_SAFE_INTEGER;
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.floor((date.getTime() - today.getTime()) / oneDay);
}

function normalizePaymentPayload(item, linkedAccountId) {
  return {
    type: item.type,
    title: item.title,
    description: item.description,
    dueDate: item.dueDate || null,
    eventDate: item.eventDate || null,
    paymentDate: item.paymentDate || null,
    startTime: item.startTime || null,
    endTime: item.endTime || null,
    recurrence: item.recurrence || "NONE",
    recurrenceEndDate: item.recurrenceEndDate || null,
    amount: item.amount ?? null,
    paymentType: item.paymentType || "BILL",
    linkedAccountId: linkedAccountId || null,
    totalEmiAmount: item.totalEmiAmount ?? null,
    remainingEmiAmount: item.remainingEmiAmount ?? null,
    emiStartDate: item.emiStartDate ?? null,
    creditCardStatementAmount: item.creditCardStatementAmount ?? null,
    creditCardMinDue: item.creditCardMinDue ?? null,
    creditCardOutstandingAmount: item.creditCardOutstandingAmount ?? null,
    creditCardStatementDate: item.creditCardStatementDate ?? null,
    priority: item.priority || "MEDIUM",
    reminderEnabled: item.reminderEnabled ?? false,
    reminderBeforeDays: item.reminderBeforeDays ?? 1,
    status: item.status || "ACTIVE",
    category: item.category || "FINANCE",
  };
}

function accountLabel(account) {
  if (!account) return "";
  const last4 = account.accountNumber ? String(account.accountNumber).slice(-4) : "";
  return `${account.accountName || account.id}${last4 ? ` • ${last4}` : ""}`;
}

export default function PaymentsDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [daysFilter, setDaysFilter] = useState(10);
  const [accountDraft, setAccountDraft] = useState({});
  const [dateDraft, setDateDraft] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [plannerItems, accountItems] = await Promise.all([getAllItems(), getAllAccounts()]);
      setItems(plannerItems || []);
      setAccounts(accountItems || []);
    } catch {
      setError("Unable to load payment dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(""), 2200);
    return () => clearTimeout(t);
  }, [success]);

  const accountMap = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      map[a.id] = a;
    });
    return map;
  }, [accounts]);

  const activePayments = useMemo(
    () =>
      items.filter(
        (i) =>
          i.type === "PAYMENT" &&
          i.status !== "DONE" &&
          i.status !== "SKIPPED" &&
          i.status !== "CANCELLED"
      ),
    [items]
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueSoon = useMemo(
    () =>
      activePayments
        .map((i) => ({ ...i, _date: parseDate(i.paymentDate || i.dueDate || i.eventDate) }))
        .filter((i) => i._date)
        .filter((i) => daysBetween(today, i._date) <= daysFilter)
        .sort((a, b) => a._date - b._date),
    [activePayments, daysFilter]
  );

  const atRisk = useMemo(
    () =>
      dueSoon.filter((i) => {
        if (!i.linkedAccountId) return false;
        const account = accountMap[i.linkedAccountId];
        const requiredAmount =
          i.paymentType === "CREDIT_CARD"
            ? Number(i.creditCardMinDue ?? i.amount ?? 0)
            : Number(i.amount ?? 0);
        if (!account || account.currentBalance == null) return false;
        return Number(account.currentBalance) < requiredAmount;
      }),
    [dueSoon, accountMap]
  );

  const doMarkPaid = async (id) => {
    setSavingId(id);
    setError("");
    try {
      await updateItemStatus(id, "DONE");
      setSuccess("Marked as paid.");
      await load();
    } catch {
      setError("Unable to mark paid.");
    } finally {
      setSavingId("");
    }
  };

  const doMoveDueDate = async (id) => {
    const newDate = dateDraft[id];
    if (!newDate) return;
    setSavingId(id);
    setError("");
    try {
      await updateItemDate(id, newDate, true);
      setSuccess("Due date updated.");
      await load();
    } catch {
      setError("Unable to move due date.");
    } finally {
      setSavingId("");
    }
  };

  const doChangeLinkedAccount = async (item) => {
    const nextAccountId = accountDraft[item.id];
    if (nextAccountId === undefined) return;
    setSavingId(item.id);
    setError("");
    try {
      await updateItem(item.id, normalizePaymentPayload(item, nextAccountId));
      setSuccess("Linked account updated.");
      await load();
    } catch {
      setError("Unable to change linked account.");
    } finally {
      setSavingId("");
    }
  };

  const PaymentCard = ({ item, showRisk }) => {
    const account = item.linkedAccountId ? accountMap[item.linkedAccountId] : null;
    const balance = account?.currentBalance;
    const amount = item.amount ?? 0;
    const requiredAmount = item.paymentType === "CREDIT_CARD"
      ? Number(item.creditCardMinDue ?? amount ?? 0)
      : Number(amount ?? 0);
    const shortfall =
      showRisk && balance != null ? Math.max(0, requiredAmount - Number(balance)) : 0;

    return (
      <div className={`payment-card ${showRisk ? "risk" : ""}`} key={item.id}>
        <div className="payment-card-top">
          <div>
            <div className="payment-title">{item.title}</div>
            <div className="payment-meta">
              {item.paymentType || "BILL"} • Due {item.paymentDate || "-"} • Amount {amount}
            </div>
          </div>
          {showRisk ? <span className="payment-risk-pill">At Risk</span> : null}
        </div>

        <div className="payment-meta">
          Linked account: {accountLabel(account) || item.linkedAccountId || "Not linked"}
          {showRisk ? ` • Balance ${balance ?? 0} • Required ${requiredAmount} • Shortfall ${shortfall}` : ""}
        </div>

        {item.paymentType === "EMI" ? (
          <div className="payment-meta">
            EMI total {item.totalEmiAmount ?? "-"} • Remaining {item.remainingEmiAmount ?? "-"} • Start {item.emiStartDate || "-"}
          </div>
        ) : null}

        {item.paymentType === "CREDIT_CARD" ? (
          <div className="payment-meta">
            Statement {item.creditCardStatementAmount ?? "-"} • Min due {item.creditCardMinDue ?? "-"} • Outstanding {item.creditCardOutstandingAmount ?? "-"} • Statement date {item.creditCardStatementDate || "-"}
          </div>
        ) : null}

        <div className="payment-actions-grid">
          <button
            type="button"
            onClick={() => doMarkPaid(item.id)}
            disabled={savingId === item.id}
            className="btn-paid"
          >
            Mark Paid
          </button>

          <div className="payment-inline-edit">
            <select
              value={accountDraft[item.id] ?? item.linkedAccountId ?? ""}
              onChange={(e) => setAccountDraft((p) => ({ ...p, [item.id]: e.target.value }))}
            >
              <option value="">No account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {accountLabel(a)}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => doChangeLinkedAccount(item)} disabled={savingId === item.id}>
              Change Linked Account
            </button>
          </div>

          <div className="payment-inline-edit">
            <input
              type="date"
              value={dateDraft[item.id] ?? item.paymentDate ?? ""}
              onChange={(e) => setDateDraft((p) => ({ ...p, [item.id]: e.target.value }))}
            />
            <button type="button" onClick={() => doMoveDueDate(item.id)} disabled={savingId === item.id}>
              Move Due Date
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="payments-wrap">
      <div className="payments-head">
        <div>
          <h2>Payments Dashboard</h2>
          <p>Track EMI, CC, subscriptions and bills with account risk visibility.</p>
        </div>
        <div className="payments-head-actions">
          <button type="button" onClick={() => nav("/planner")}>Back</button>
          <button type="button" onClick={load}>Refresh</button>
        </div>
      </div>

      <div className="payments-filters">
        <label>
          Due in next
          <select value={daysFilter} onChange={(e) => setDaysFilter(Number(e.target.value))}>
            <option value={3}>3 days</option>
            <option value={5}>5 days</option>
            <option value={10}>10 days</option>
            <option value={15}>15 days</option>
            <option value={30}>30 days</option>
          </select>
        </label>
      </div>

      {loading ? <p>Loading payment dashboard...</p> : null}
      {error ? <p className="payments-error">{error}</p> : null}
      {success ? <p className="payments-success">{success}</p> : null}

      {!loading && (
        <div className="payments-grid">
          <section className="payments-panel">
            <h3>At Risk ({atRisk.length})</h3>
            {atRisk.length === 0 ? (
              <p className="payments-muted">No low-balance payment risks in selected range.</p>
            ) : (
              atRisk.map((item) => <PaymentCard key={item.id} item={item} showRisk={true} />)
            )}
          </section>

          <section className="payments-panel">
            <h3>Due Soon ({dueSoon.length})</h3>
            {dueSoon.length === 0 ? (
              <p className="payments-muted">No upcoming payments.</p>
            ) : (
              dueSoon.map((item) => <PaymentCard key={item.id} item={item} showRisk={false} />)
            )}
          </section>
        </div>
      )}
    </div>
  );
}
